import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface Workspace {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

interface WorkspaceStored {
  id: string;
  name: string;
}

interface WorkspaceState {
  selectedWorkspace: Workspace | null;
  workspaces: Workspace[];
  setSelectedWorkspace: (workspace: Workspace | null) => void;
  setWorkspaces: (workspaces: Workspace[]) => void;
  clearWorkspace: () => void;
  validateAndFixWorkspace: (validWorkspaces: Workspace[]) => void;
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      selectedWorkspace: null,
      workspaces: [],
      setSelectedWorkspace: (workspace) => set({ selectedWorkspace: workspace }),
      setWorkspaces: (workspaces) => set({ workspaces }),
      clearWorkspace: () => set({ selectedWorkspace: null, workspaces: [] }),
      validateAndFixWorkspace: (validWorkspaces) => {
        const { selectedWorkspace } = get();
        if (selectedWorkspace) {
          const stillExists = validWorkspaces.some(w => w.id === selectedWorkspace.id);
          if (!stillExists) {
            console.warn(`[WorkspaceStore] Invalid workspace ${selectedWorkspace.id} cleared`);
            set({ selectedWorkspace: null });
            // Auto-select first valid one
            if (validWorkspaces.length > 0) {
              set({ selectedWorkspace: validWorkspaces[0] });
            }
          }
        } else if (validWorkspaces.length > 0) {
          set({ selectedWorkspace: validWorkspaces[0] });
        }
      },
    }),
    {
      name: 'workspace-storage',
      partialize: (state) => ({
        selectedWorkspace: state.selectedWorkspace
          ? { id: state.selectedWorkspace.id, name: state.selectedWorkspace.name }
          : null,
        workspaces: state.workspaces.map(({ id, name }) => ({ id, name })),
      }),
      merge: (persisted, current) => {
        const stored = persisted as { selectedWorkspace: WorkspaceStored | null; workspaces: WorkspaceStored[] };
        if (!stored) return current;
        const fallbackDate = new Date(0).toISOString();
        return {
          ...current,
          selectedWorkspace: stored.selectedWorkspace
            ? { ...stored.selectedWorkspace, createdAt: fallbackDate, updatedAt: fallbackDate } as Workspace
            : null,
          workspaces: stored.workspaces
            ? stored.workspaces.map(w => ({ ...w, createdAt: fallbackDate, updatedAt: fallbackDate }) as Workspace)
            : [],
        };
      },
    }
  )
);
